import {
  Card,
  Flex,
  FormLayout,
  InfoTooltip,
  Text,
  Tooltip,
  UnitField,
  useMediaQuery,
  useToast,
} from "@wayflyer/flyui";
import debounce from "lodash.debounce";
import * as React from "react";
import { useIntl } from "react-intl";

import {
  CollapsedContainer,
  CollapsedValuesContainer,
  CollapsedValuesItemContainer,
  CollapsedValuesItemTitle,
  CollapsedValuesItemValue,
  CollapseIcon,
  CollapseIconContainer,
  ExpandIcon,
  ExpandIconContainer,
  ExpandIconMobile,
  FormContainer,
  UncollapsedContainer,
} from "../../components/collapsing-container/styled";
import type { AdditionalCostsData } from "../../components/shared";
import { FullWidthButton } from "../../components/styled";

export interface AdditionalCostsFormProps {
  currencySymbol: string;
  saveHandler: (data: AdditionalCostsData) => Promise<void>;
  additionalCostsData: AdditionalCostsData;
  additionalCostsDataError?: string;
  onChangeHandler: (field: keyof AdditionalCostsData, value: number) => void;
  expanded?: boolean;
  waitTimeForUpdate?: number; // milliseconds
  embedded?: boolean;
}

/**
 * AdditionalCostsForm component
 */
export const AdditionalCostsForm = ({
  currencySymbol,
  saveHandler,
  additionalCostsData,
  additionalCostsDataError,
  onChangeHandler,
  expanded = true,
  waitTimeForUpdate = 500,
  embedded = false,
}: AdditionalCostsFormProps): UIElement => {
  const intl = useIntl();
  const toaster = useToast();
  const query = useMediaQuery();
  const mediumWidth = query("sm").windowGt;

  const [saving, setSaving] = React.useState(false);
  const [cogsString, setCogsString] = React.useState(additionalCostsData.cogs.toString());
  const [shippingString, setShippingString] = React.useState(additionalCostsData.shipping.toString());
  const [opexString, setOpexString] = React.useState(additionalCostsData.opex.toString());
  const [marketingString, setMarketingString] = React.useState(additionalCostsData.marketing.toString());
  const [isExpanded, setIsExpanded] = React.useState(expanded);
  const changeHandler = React.useMemo(
    () =>
      debounce((field: keyof AdditionalCostsData, value: number) => {
        onChangeHandler(field, value);
      }, waitTimeForUpdate),
    [onChangeHandler, waitTimeForUpdate]
  );

  const getData = () => {
    return {
      cogs: Number(cogsString),
      shipping: Number(shippingString),
      opex: Number(opexString),
      marketing: Number(marketingString),
    };
  };

  const handleOnChange = (name, setFn) => (event) => {
    if (!isNaN(event.target.value)) {
      setFn(event.target.value);
      changeHandler(name, Number(event.target.value));
    }
  };

  const onSave = () => {
    setSaving(true);
    saveHandler(getData())
      .then(() => {
        toaster.positive(
          intl.formatMessage({
            id: "Additional costs saved successfully",
            defaultMessage: "The additional costs have been saved successfully",
          })
        );
        setSaving(false);
        setIsExpanded(false);
      })
      .catch(() => {
        setSaving(false);
        toaster.critical(
          intl.formatMessage({
            id: "Error with saving additional costs",
            defaultMessage:
              "There was an error saving the additional costs. If the problem persists please contact support.",
          })
        );
      });
  };

  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    setCogsString(additionalCostsData.cogs.toString());
    setShippingString(additionalCostsData.shipping.toString());
    setOpexString(additionalCostsData.opex.toString());
    setMarketingString(additionalCostsData.marketing.toString());
  }, [additionalCostsData]);

  const ExpandedContainer = embedded ? React.Fragment : UncollapsedContainer;
  const FieldsContainer = embedded ? React.Fragment : FormContainer;
  if (embedded && !isExpanded) {
    return (
      <Flex direction="column" gap="4">
        <Text>{`${intl.formatMessage({
          id: "Additional costs : CoGS collapsed",
          defaultMessage: "CoGS",
        })}: ${currencySymbol}${cogsString}`}</Text>
        <Text>{`${intl.formatMessage({
          id: "Additional costs : Shipping collapsed",
          defaultMessage: "Shipping",
        })}: ${currencySymbol}${shippingString}`}</Text>
        <Text>{`${intl.formatMessage({
          id: "Additional costs : OPEX collapsed",
          defaultMessage: "OPEX",
        })}: ${currencySymbol}${opexString}`}</Text>
        <Text>{`${intl.formatMessage({
          id: "Additional costs : Marketing collapsed",
          defaultMessage: "Marketing",
        })}: ${currencySymbol}${marketingString}`}</Text>
        <Card.HeaderActions
          initialAsButton
          actions={[
            {
              content: intl.formatMessage({ id: "Overview: edit costs", defaultMessage: "Edit additional costs" }),
              onAction: () => setIsExpanded(true),
            },
          ]}
        />
      </Flex>
    );
  }
  return isExpanded ? (
    <ExpandedContainer>
      <FieldsContainer>
        <form>
          <FormLayout>
            <Flex wrap="wrap" direction={embedded ? "column" : undefined} gap={embedded ? "2" : undefined}>
              {embedded ? (
                <Card.HeaderActions
                  initialAsButton
                  actions={[
                    {
                      content: intl.formatMessage({
                        id: "Overview: hide costs",
                        defaultMessage: "Hide additional costs",
                      }),
                      onAction: () => setIsExpanded(false),
                    },
                  ]}
                />
              ) : (
                <CollapseIconContainer>
                  <Flex wrap="nowrap" gap="1" align="center">
                    <Text>
                      {intl.formatMessage({ id: "Additional Costs title", defaultMessage: "Additional Costs" })}
                    </Text>
                    <InfoTooltip
                      content={intl.formatMessage({
                        id: "Additional costs tooltip",
                        defaultMessage:
                          "Use this form to add any additional costs that arent captured from your shopping or ads platforms. Once entered a profitability metric will be added to the graph and table.",
                      })}
                      iconSize="regular"
                    />
                  </Flex>
                  <Tooltip
                    content={intl.formatMessage({
                      id: "Additional costs hide message",
                      defaultMessage: "Click here to hide the Additional Costs form",
                    })}
                  >
                    <CollapseIcon
                      size={16}
                      role="button"
                      onClick={() => setIsExpanded(false)}
                      onKeyDown={() => setIsExpanded(false)}
                    />
                  </Tooltip>
                </CollapseIconContainer>
              )}
              <Text>
                {intl.formatMessage({ id: "Additional costs entry message", defaultMessage: "Enter average values" })}
              </Text>
            </Flex>
            {additionalCostsDataError ? (
              <Text color="critical">
                {intl.formatMessage(
                  {
                    id: "Additional costs error message",
                    defaultMessage: "Could not load any previously configured costs: {error}",
                  },
                  { error: additionalCostsDataError }
                )}
              </Text>
            ) : null}

            <ActualInput
              name="cogs"
              label={intl.formatMessage({ id: "Additional costs: CoGS per item", defaultMessage: "CoGS per item" })}
              tooltip={intl.formatMessage({
                id: "Additional costs CoGS message",
                defaultMessage: "Average Cost of Goods Sold per product",
              })}
              value={cogsString}
              currencySymbol={currencySymbol}
              handleOnChange={handleOnChange("cogs", setCogsString)}
            />
            <ActualInput
              name="shipping"
              label={intl.formatMessage({
                id: "Additional costs Shipping cost per item",
                defaultMessage: "Shipping cost per item",
              })}
              tooltip={intl.formatMessage({
                id: "Additional costs Average Shipping cost per product",
                defaultMessage: "Average Shipping cost per product",
              })}
              value={shippingString}
              currencySymbol={currencySymbol}
              handleOnChange={handleOnChange("shipping", setShippingString)}
            />
            <ActualInput
              name="opex"
              label={intl.formatMessage({ id: "Additional costs OpEx", defaultMessage: "OpEx" })}
              tooltip={intl.formatMessage({
                id: "Additional costs opex tooltip",
                defaultMessage: "Average monthly Operational Expense",
              })}
              value={opexString}
              currencySymbol={currencySymbol}
              handleOnChange={handleOnChange("opex", setOpexString)}
            />
            <ActualInput
              name="marketing"
              label={intl.formatMessage({
                id: "Additional costs additional marketing",
                defaultMessage: "Additional Marketing",
              })}
              tooltip={intl.formatMessage({
                id: "Additional costs additional marketing tooltip",
                defaultMessage: "Average Monthly Marketing spend excluding Facebook and Google ads if connected",
              })}
              value={marketingString}
              currencySymbol={currencySymbol}
              handleOnChange={handleOnChange("marketing", setMarketingString)}
            />
            <FullWidthButton
              variant="Primary"
              title={intl.formatMessage({ id: "Additional costs save button", defaultMessage: "Save" })}
              onClick={onSave}
              loading={saving}
            >
              {intl.formatMessage({ id: "Additional costs save button", defaultMessage: "Save" })}
            </FullWidthButton>
          </FormLayout>
        </form>
      </FieldsContainer>
    </ExpandedContainer>
  ) : (
    <CollapsedContainer>
      <Tooltip
        content={intl.formatMessage({
          id: "Additional costs open message",
          defaultMessage: "Click here to open the Additional Costs form",
        })}
      >
        <ExpandIconContainer
          role="button"
          tabIndex={-1}
          isBottom={false}
          onClick={() => setIsExpanded(true)}
          onKeyDown={() => setIsExpanded(false)}
        >
          {mediumWidth ? <ExpandIcon /> : <ExpandIconMobile />}
        </ExpandIconContainer>
      </Tooltip>
      <CollapsedValuesContainer>
        <CollapsedValuesItemContainer>
          <CollapsedValuesItemValue>{`${currencySymbol}${cogsString}`}</CollapsedValuesItemValue>
          <CollapsedValuesItemTitle>
            {intl.formatMessage({ id: "Additional costs : CoGS collapsed", defaultMessage: "CoGS" })}
          </CollapsedValuesItemTitle>
        </CollapsedValuesItemContainer>
        <CollapsedValuesItemContainer>
          <CollapsedValuesItemValue>{`${currencySymbol}${shippingString}`}</CollapsedValuesItemValue>
          <CollapsedValuesItemTitle>
            {intl.formatMessage({ id: "Additional costs : Shipping collapsed", defaultMessage: "Shipping" })}
          </CollapsedValuesItemTitle>
        </CollapsedValuesItemContainer>
        <CollapsedValuesItemContainer>
          <CollapsedValuesItemValue>{`${currencySymbol}${opexString}`}</CollapsedValuesItemValue>
          <CollapsedValuesItemTitle>
            {intl.formatMessage({ id: "Additional costs : OPEX collapsed", defaultMessage: "OPEX" })}
          </CollapsedValuesItemTitle>
        </CollapsedValuesItemContainer>
        <CollapsedValuesItemContainer>
          <CollapsedValuesItemValue>{`${currencySymbol}${marketingString}`}</CollapsedValuesItemValue>
          <CollapsedValuesItemTitle>
            {intl.formatMessage({ id: "Additional costs : Marketing collapsed", defaultMessage: "Marketing" })}
          </CollapsedValuesItemTitle>
        </CollapsedValuesItemContainer>
      </CollapsedValuesContainer>
      <Tooltip
        content={intl.formatMessage({
          id: "Open Additional Costs tooltip",
          defaultMessage: "Click here to open the Additional Costs form",
        })}
      >
        <ExpandIconContainer
          role="button"
          tabIndex={-1}
          isBottom={true}
          onClick={() => setIsExpanded(true)}
          onKeyDown={() => setIsExpanded(false)}
        >
          {mediumWidth ? <ExpandIcon /> : <ExpandIconMobile />}
        </ExpandIconContainer>
      </Tooltip>
    </CollapsedContainer>
  );
};

interface ActualInputProps {
  name: string;
  label: string;
  value: string | number;
  currencySymbol: string;
  tooltip: string;
  handleOnChange: (event) => void;
}

const ActualInput = ({ name, label, value, currencySymbol, tooltip, handleOnChange }: ActualInputProps) => {
  return (
    <UnitField
      label={label}
      tooltip={tooltip}
      value={`${value}`}
      name={name}
      onChange={handleOnChange}
      unitLabel={currencySymbol}
    />
  );
};
